/**
 * Background Image Configuration for Bio Template
 * Provides dynamic background images based on country and device type
 */

import { getDeviceInfo } from './deviceDetection';

// Background image URLs provided by user
const BACKGROUND_IMAGES = {
  desktop: 'https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/607db50b-e13b-49b8-60a7-58e1c419ea00/public',
  mobile: 'https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/5722ef64-fd7d-420c-efc3-b91ea5a6bd00/public'
} as const;

export interface BackgroundConfig {
  imageUrl: string;
  deviceType: 'desktop' | 'mobile';
  country: string;
  isSupported: boolean;
}

/**
 * Get background image URL based on country and device type
 * Currently supports US and Spain with the same images
 * Can be extended for country-specific images in the future
 */
export function getBioTemplateBackground(country?: string, deviceType?: 'desktop' | 'mobile'): BackgroundConfig {
  // Get device type if not provided
  const detectedDeviceType = deviceType || (getDeviceInfo().isMobile ? 'mobile' : 'desktop');
  
  // Supported countries (US and Spain as specified)
  const supportedCountries = ['US', 'ES'];
  const isSupported = country ? supportedCountries.includes(country) : true;
  
  // For now, both US and Spain use the same images
  // This can be extended later for country-specific backgrounds
  const imageUrl = detectedDeviceType === 'mobile' 
    ? BACKGROUND_IMAGES.mobile 
    : BACKGROUND_IMAGES.desktop;
  
  return {
    imageUrl,
    deviceType: detectedDeviceType,
    country: country || 'UNKNOWN',
    isSupported
  };
}

/**
 * Get CSS background style for the Bio Template
 */
export function getBioTemplateBackgroundStyle(country?: string, deviceType?: 'desktop' | 'mobile'): React.CSSProperties {
  const config = getBioTemplateBackground(country, deviceType);
  
  return {
    backgroundImage: `url(${config.imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    // Use scroll instead of fixed for better LCP performance
    backgroundAttachment: 'scroll',
    // Performance optimizations for faster rendering
    willChange: 'auto',
    // Optimize image loading
    imageRendering: 'auto',
  };
}

/**
 * Preload background images for better performance
 * Note: Critical images are now preloaded in index.html for better LCP
 * This function is kept for fallback/edge cases
 */
export function preloadBioTemplateBackgrounds(): void {
  if (typeof window === 'undefined') return;
  
  // Check if images are already preloaded via link tags
  const existingPreloads = document.querySelectorAll('link[rel="preload"][as="image"]');
  const preloadedUrls = Array.from(existingPreloads).map(link => (link as HTMLLinkElement).href);
  
  Object.values(BACKGROUND_IMAGES).forEach(imageUrl => {
    // Only preload if not already preloaded in HTML
    if (!preloadedUrls.includes(imageUrl)) {
      const img = new Image();
      img.src = imageUrl;
      img.fetchPriority = 'high';
    }
  });
}

/**
 * Get responsive background image URLs for different screen sizes
 * Useful for responsive images or picture elements
 */
export function getResponsiveBioTemplateBackgrounds(country?: string) {
  const config = getBioTemplateBackground(country);
  
  return {
    mobile: BACKGROUND_IMAGES.mobile,
    desktop: BACKGROUND_IMAGES.desktop,
    current: config.imageUrl,
    isSupported: config.isSupported
  };
}

export default {
  getBioTemplateBackground,
  getBioTemplateBackgroundStyle,
  preloadBioTemplateBackgrounds,
  getResponsiveBioTemplateBackgrounds,
  BACKGROUND_IMAGES
};